/**
 * One collapsible top-level section on the Atlas console — shared so every
 * section behaves and aligns identically (a chevron rail on the left, the
 * title + an optional inline summary, and optional right-aligned actions).
 *
 * Alignment contract: the chevron lives in a fixed-width rail (RAIL); the
 * title starts at the rail's right edge and the body is indented to the same
 * x. So across the whole panel, every chevron lines up in one gutter, every
 * title lines up in one column, and every body lines up under its title —
 * with comfortable breathing room from the panel's left border.
 *
 * Controlled or uncontrolled: pass `expanded` + `onExpandedChange` to drive
 * it (the template section force-expands when you pick a historical version);
 * otherwise it manages its own state from `defaultExpanded`.
 */
import { useState, type ReactNode } from 'react';
import { css, SerializedStyles } from '@emotion/react';
import { Box, Collapse, IconButton, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { colors } from '../../theme/colors';

/** Chevron-rail width. Title and body both align to its right edge. */
const RAIL = '1.75rem';

/** Greyed-out opacity for a section with no data yet (see `disabledHint`). */
const DISABLED_OPACITY = 0.55;

const titleCss = css`
  font-size: 16px;
  font-weight: 700;
  color: ${colors.text.main};
  letter-spacing: -0.01em;
  cursor: pointer;
  user-select: none;
`;

interface CollapsibleSectionProps {
  title: string;
  /** Initial state when uncontrolled. Ignored if `expanded` is passed. */
  defaultExpanded?: boolean;
  /** Controlled open state. Provide together with `onExpandedChange`. */
  expanded?: boolean;
  onExpandedChange?: (next: boolean) => void;
  /** Muted text/chips shown inline after the title (always visible). */
  summary?: ReactNode;
  /** Right-aligned controls (always visible; clicking them doesn't toggle). */
  actions?: ReactNode;
  /** When set, the section title/body are greyed-out and this hint renders as
   *  the body instead of `children` — the shared "no data yet" state. `actions`
   *  stay at full opacity: they remain interactive (e.g. a Run button that can
   *  populate the section) and must not read as disabled. */
  disabledHint?: ReactNode;
  /** Keep the body mounted after its first expand (still deferred until then) —
   *  for stateful children like grids that shouldn't reset on toggle. Default
   *  unmounts on collapse. */
  keepMounted?: boolean;
  /** Layered over the default 16px/700 title (e.g. the Deals cards run smaller). */
  titleCss?: SerializedStyles;
  children?: ReactNode;
}

export function CollapsibleSection({
  title,
  defaultExpanded = true,
  expanded: controlledExpanded,
  onExpandedChange,
  summary,
  actions,
  disabledHint,
  keepMounted = false,
  titleCss: titleCssOverride,
  children,
}: CollapsibleSectionProps) {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isControlled = controlledExpanded !== undefined;
  const expanded = isControlled ? controlledExpanded : internalExpanded;

  const toggle = () => {
    const next = !expanded;
    if (!isControlled) {
      setInternalExpanded(next);
    }
    onExpandedChange?.(next);
  };

  const disabled = disabledHint !== undefined;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Left group: chevron rail + title + summary. Whole group toggles.
            Disabled dimming applies here and to the body — never to `actions`. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            minWidth: 0,
            cursor: 'pointer',
            ...(disabled ? { opacity: DISABLED_OPACITY } : {}),
          }}
          onClick={toggle}
        >
          <Box sx={{ width: RAIL, flexShrink: 0, display: 'flex' }}>
            <IconButton
              size='small'
              aria-label={`${expanded ? 'collapse' : 'expand'} ${title}`}
              aria-expanded={expanded}
              onClick={(e) => {
                // Stop the parent's toggle from also firing (double-toggle).
                e.stopPropagation();
                toggle();
              }}
              sx={{ padding: 0.25 }}
            >
              {/* Right while collapsed, down while expanded: the app's disclosure convention. */}
              <ChevronRightIcon
                fontSize='small'
                sx={{
                  transform: expanded ? 'rotate(90deg)' : 'none',
                  transition: 'transform 0.15s',
                }}
              />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Typography css={[titleCss, titleCssOverride]}>{title}</Typography>
            {summary}
          </Box>
        </Box>
        {actions && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>{actions}</Box>
        )}
      </Box>
      {/* Body indented to the rail so it lines up under the title. Animated
       *  reveal via MUI Collapse (the house pattern). Both modes defer the
       *  initial mount; keepMounted then preserves the body across toggles. */}
      <Collapse in={expanded} timeout='auto' mountOnEnter={keepMounted} unmountOnExit={!keepMounted}>
        <Box sx={{ paddingLeft: RAIL, marginTop: 0.5, ...(disabled ? { opacity: DISABLED_OPACITY } : {}) }}>
          {disabled ? (
            <Typography variant='caption' sx={{ color: colors.neutral[50], fontStyle: 'italic' }}>
              {disabledHint}
            </Typography>
          ) : (
            children
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
